/**
 * A mapping of application routes to their corresponding i18n translation module names.
 * Each route is mapped to an array of module names that should be loaded for that route.
 *
 * @type {Record<string, string[]>}
 * @property {string[]} [key] - The application route path
 * @property {string[]} [value] - Array of module names to load for the route
 *                                Always includes 'generic' as the base module
 *                                followed by route-specific module names
 */
export const routeModuleMap: Record<string, string[]> = {
  // Must match the backend UILM module names the Angular app registers
  // (TranslationResourceModelV2 calls in its *.module.ts files) -- never
  // invent new module names here. Public/auth routes fall back to
  // LanguageProvider's `defaultModules` prop (src/App.tsx) when they have no
  // entry of their own here -- keep that prop in sync with 'generic', not a
  // typo'd/invented name, or every route below (and any route missing from
  // this map) silently stops picking up language changes.
  '/login': ['generic', 'auth'],
  '/oidc/activate': ['generic', 'auth'],
  '/activate': ['generic', 'auth'],
  '/success': ['generic', 'auth'],
  '/activate-failed': ['generic', 'auth'],
  '/profile': ['generic', 'uam', 'profiles', 'kpi-management'],
  // getMatchedModules() (language-context.tsx) does a longest-prefix match,
  // so each /hr/* feature can (and should) get its own entry here instead of
  // being collapsed into one '/hr' catch-all -- that keeps every route only
  // loading the modules it actually needs, mirroring how the Angular app
  // registers translations per feature module (its
  // TranslationResourceModelV2(...) calls). '/hr' below is just the
  // fallback for any /hr/* route not (yet) listed explicitly.
  '/hr/department-manager': ['generic', 'uam', 'department-manager'],
  '/hr/role-manager': ['generic', 'role-manager'],
  '/hr/designation-manager': ['generic', 'designation-manager'],
  '/hr/location-manager': ['generic', 'location-manager'],
  '/hr/project-manager': ['generic', 'uam', 'project'],
  // The Angular kpi-configuration feature's strings live in 'performance-management' (KPI_CONFIG_TITLE,
  // MAX, ...) and its toast/validation strings in 'kpi-management', so both modules are needed here.
  '/hr/kpi-configuration': ['generic', 'kpi-management', 'performance-management'],
  '/hr/skill-manager': ['generic', 'kpi-management'],
  '/hr/noc-type-manager': ['generic', 'service-desk'],
  '/hr/ctc-level-manager': ['generic', 'uam'],
  '/hr/directory': ['generic', 'uam'],
  '/hr/my-attendance': ['generic', 'attendance', 'overtime-management'],
  '/hr/manage-attendance': ['generic', 'attendance', 'overtime-management'],
  '/hr/leave-builder': ['generic', 'leave-management'],
  '/hr/holiday': ['generic', 'leave-management'],
  '/hr/my-leave': ['generic', 'leave-management'],
  '/hr/leave-calendar': ['generic', 'leave-management'],
  '/hr/leave-manager': ['generic', 'leave-management'],
  '/hr/sick-leave': ['generic', 'leave-management'],
  '/hr/my-toil': ['generic', 'toil-management'],
  '/hr/toil-manager': ['generic', 'toil-management'],
  '/hr/overtime-manager': ['generic', 'overtime-management'],
  '/hr/my-timesheet': ['generic', 'timesheet-management'],
  '/hr/timesheet-manager': ['generic', 'timesheet-management'],
  '/hr/mbm-project-manager': ['generic', 'timesheet-management'],
  '/hr/form-creation': ['generic', 'performance-management'],
  '/hr/reviews': ['generic', 'performance-management'],
  '/hr/my-submissions': ['generic', 'performance-management'],
  '/hr/peer-requests': ['generic', 'performance-management'],
  '/hr/request-lunch': ['generic', 'lunch-system'],
  '/hr/manage-lunch': ['generic', 'lunch-system'],
  '/hr/potential-profiles': ['generic', 'profiles'],
  '/hr/conditional-offers': ['generic', 'profiles'],
  '/hr/survey-form-creation': ['generic', 'performance-management', 'survey'],
  '/hr/surveys': ['generic', 'performance-management', 'survey'],
  '/hr/survey-my-submissions': ['generic', 'performance-management', 'survey'],
  '/hr/service-desk': ['generic', 'service-desk'],
  '/hr/performance-dashboard': ['generic', 'performance-management'],
  '/logs': ['generic', 'logs'],
  '/hr': ['generic'],
};
